import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import apiReports, { IOfferSalesOffer, IOfferSalesWorkType } from 'api/reports';
import PageContainer from 'components/layout/PageContainer';
import CurrencyToggle from 'components/offers/src/CurrencyToggle';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import { useReportCurrency } from 'services/offers/exchangeRate';
import getOfferStatusLabel from 'services/offers/offerStatusLabel';
import translations from 'services/translations/translations';
import { isOrderedStatus } from 'services/enums/offers/Statuses';
import { addMessage } from 'store/messages/actions';

import {
  BarList,
  ISeries,
  IStackedColumn,
  Legend,
  StackedColumns,
} from './DashboardCharts';
import MultiSelectFilter, { IFilterOption } from './MultiSelectFilter';
import { daysOf, monthsOf } from './reportPeriods';
import useReportPeriod from './ReportPeriodPicker';

import 'style/reports/OfferDashboard.css';

// The phases of the report: offers and preliminary offers being calculated and sent (waiting
// for an answer), and the ordered ones. A material offer is an offer here.
const PHASE_KEYS = [
  'offer-sent',
  'offer-calculating',
  'preliminary-sent',
  'preliminary-calculating',
  'ordered',
];
const PHASE_COLORS: { [phase: string]: string } = {
  'offer-calculating': '#56ccf2',
  'preliminary-calculating': '#9bd8ee',
  'offer-sent': '#2f80ed',
  'preliminary-sent': '#9b51e0',
  ordered: '#27ae60',
};

// The rows shown before "Show all"
const ROWS_SHOWN = 50;

// A period of at most this many days is charted by day, a longer one by month
const MAX_DAY_COLUMNS = 31;

// The signers in the chart
const SIGNERS_CHARTED = 12;

const phaseOf = (offer: { status: string; type: string }): string => {
  if (isOrderedStatus(offer.status)) {
    return 'ordered';
  }

  const kind = offer.type === 'preliminary-offer' ? 'preliminary' : 'offer';
  return `${kind}-${offer.status === 'sent' ? 'sent' : 'calculating'}`;
};

// The margin % marks of the calculation window: under 20 % red, over 30 % green
const getMarginColor = (ratio: null | number): string => {
  if (ratio === null) {
    return '#b8c0c8';
  }

  if (ratio < 20) {
    return '#eb5757';
  }

  return ratio > 30 ? '#27ae60' : '#e2b93b';
};

const getMarginRatio = (value: number, expense: number): null | number =>
  value > 0 ? ((value - expense) / value) * 100 : null;

type SortKey =
  | 'customerName'
  | 'departmentName'
  | 'expense'
  | 'margin'
  | 'marginRatio'
  | 'offeredBy'
  | 'reportedAt'
  | 'status'
  | 'targetName'
  | 'type'
  | 'value'
  | 'workType';

// The sorts of numbers and days start from the largest and latest
const DESCENDING_FIRST: SortKey[] = [
  'expense',
  'margin',
  'marginRatio',
  'reportedAt',
  'value',
];

// An offer of the table with the sums shown: the whole offer, or only the work types searched
interface IRow {
  expense: null | number;
  offer: IOfferSalesOffer;
  value: null | number;
  // The work types of the row: all of the offer, or those searched
  workTypes: IOfferSalesWorkType[];
}

// The value of the work type choice of the positions without one (their group is empty)
const NO_WORK_TYPE_VALUE = '-';
const workTypeValue = (group: string): string => group || NO_WORK_TYPE_VALUE;

interface ISums {
  count: number;
  expense: number;
  // The margin of the rows with a known expense (an offer of SalesPro may have none)
  margin: number;
  // The sales of the rows with a known expense, the base of the margin %
  marginBase: number;
  value: number;
}

const sumOf = (rows: IRow[]): ISums =>
  rows.reduce(
    (sums, row) => {
      const known = row.expense !== null && row.value !== null;
      return {
        count: sums.count + 1,
        expense: sums.expense + (row.expense ?? 0),
        margin:
          sums.margin + (known ? (row.value ?? 0) - (row.expense ?? 0) : 0),
        marginBase: sums.marginBase + (known ? row.value ?? 0 : 0),
        value: sums.value + (row.value ?? 0),
      };
    },
    { count: 0, expense: 0, margin: 0, marginBase: 0, value: 0 }
  );

const ratioOf = (sums: ISums): null | number =>
  sums.marginBase > 0 ? (sums.margin / sums.marginBase) * 100 : null;

const isSalesPro = (offer: IOfferSalesOffer): boolean =>
  offer.source === 'salespro';

// A key of a row: an offer of SalesPro may have the same id as one of ProBid
const rowKey = (offer: IOfferSalesOffer): string =>
  `${offer.source ?? 'probid'}-${offer.id}`;

// Keeping the choice to show the offers of SalesPro in the browser; storage may be blocked
const SALES_PRO_STORAGE_KEY = 'offer-sales-include-salespro';
const readIncludeSalesPro = (): boolean => {
  try {
    return window.localStorage.getItem(SALES_PRO_STORAGE_KEY) === 'true';
  } catch (error) {
    return false;
  }
};
const writeIncludeSalesPro = (value: boolean) => {
  try {
    window.localStorage.setItem(SALES_PRO_STORAGE_KEY, String(value));
  } catch (error) {
    // The choice is not kept
  }
};

/**
 * The sales report: the preliminary offers, offers and material offers being calculated (by
 * the day they were created), sent (by the day they were sent) and ordered (by the day they
 * were ordered) during the period, the latest revision of each. Filtered by department,
 * customer, signer, work type and status (many of each with Ctrl), sorted by any column,
 * and the chosen view saved as an Excel file. An offer of many work types shows its whole
 * sum and opens by its + into a row for each work type; searching work types leaves only
 * their sums of each offer.
 */
const OfferSales = ({ tabs }: { tabs?: JSX.Element }): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation([
    translations.offers,
    translations.products,
    translations.common,
  ]);
  const tr = (key: string, options?: Record<string, unknown>) =>
    t(`${translations.offers}:dashboard.sales.${key}`, options);

  const { period, picker } = useReportPeriod('offer-sales');
  const [data, setData] = useState<IOfferSalesOffer[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [exporting, setExporting] = useState(false);
  // The open offers imported from SalesPro with those of ProBid
  const [includeSalesPro, setIncludeSalesPro] = useState(readIncludeSalesPro);
  const toggleSalesPro = () => {
    writeIncludeSalesPro(!includeSalesPro);
    setIncludeSalesPro(!includeSalesPro);
  };

  // The filters, the values chosen in each; none chosen is all
  const [phases, setPhases] = useState<string[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [customers, setCustomers] = useState<string[]>([]);
  const [signers, setSigners] = useState<string[]>([]);
  const [workTypes, setWorkTypes] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>('reportedAt');
  const [sortAscending, setSortAscending] = useState(false);
  const [showAll, setShowAll] = useState(false);
  // The offers opened into rows by work type
  const [opened, setOpened] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);

    apiReports
      .getOfferSales(period.from, period.to, includeSalesPro)
      .then((result) => {
        if (!cancelled) {
          setData(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [period, includeSalesPro]);

  const noDepartment = tr('No department');
  const departmentOf = (offer: IOfferSalesOffer) =>
    offer.departmentName ?? noDepartment;
  const customerOf = (offer: IOfferSalesOffer) => offer.customerName ?? '';
  const offeredByOf = (offer: IOfferSalesOffer) => offer.offeredBy ?? '';
  const typeLabel = (offerType: string) =>
    t(`${translations.offers}:types.${offerType}`);
  const workTypeLabel = (group: string) =>
    group ? t(`${translations.products}:groups.${group}`) : tr('No work type');
  const workTypesLabel = (groups: IOfferSalesWorkType[]) =>
    groups.length === 1
      ? workTypeLabel(groups[0].group)
      : tr('Work types', { count: groups.length });
  const isChosen = (chosen: string[], value: string) =>
    chosen.length === 0 || chosen.includes(value);

  // The sums in one currency: euros, or kronas when only Swedish offers are viewed
  const report = useReportCurrency(
    (data ?? [])
      .filter((offer) => isChosen(departments, departmentOf(offer)))
      .map((offer) => offer.country)
  );
  const money = (value: number) =>
    `${formatGroupedNumber(value, 0, 0)} ${report.symbol}`;

  const offers = useMemo(
    () =>
      (data ?? []).map((offer) => {
        const convert = (value: number) =>
          report.convertFrom(offer.country, value);
        return {
          ...offer,
          expense: offer.expense === null ? null : convert(offer.expense),
          value: offer.value === null ? null : convert(offer.value),
          workTypes: offer.workTypes.map((group) => ({
            ...group,
            expense: convert(group.expense),
            value: convert(group.value),
          })),
        };
      }),
    [data, report.currency, report.rate]
  );

  // The choices of each filter from the offers of the period
  const distinct = (values: string[]) =>
    Array.from(new Set(values.filter((value) => value !== ''))).sort((a, b) =>
      a.localeCompare(b, 'fi')
    );
  const departmentOptions = distinct(offers.map(departmentOf));
  const customerOptions = distinct(offers.map(customerOf));
  const signerOptions = distinct(offers.map(offeredByOf));
  const workTypeOptions = Array.from(
    new Set(
      offers.flatMap((offer) => offer.workTypes.map((group) => group.group))
    )
  ).sort((a, b) => workTypeLabel(a).localeCompare(workTypeLabel(b), 'fi'));

  // The rows of the other filters, the sums of the work types searched; the phase is left
  // out so its totals stay visible
  const withoutPhase: IRow[] = offers
    .filter(
      (offer) =>
        isChosen(departments, departmentOf(offer)) &&
        isChosen(customers, customerOf(offer)) &&
        isChosen(signers, offeredByOf(offer))
    )
    .flatMap((offer): IRow[] => {
      if (workTypes.length === 0) {
        return [
          {
            expense: offer.expense,
            offer,
            value: offer.value,
            workTypes: offer.workTypes,
          },
        ];
      }

      const searched = offer.workTypes.filter((group) =>
        workTypes.includes(workTypeValue(group.group))
      );
      return searched.length > 0
        ? [
            {
              expense: searched.reduce((sum, group) => sum + group.expense, 0),
              offer,
              value: searched.reduce((sum, group) => sum + group.value, 0),
              workTypes: searched,
            },
          ]
        : [];
    });
  const filtered = withoutPhase.filter(
    (row) => phases.length === 0 || phases.includes(phaseOf(row.offer))
  );

  const sortValue = (row: IRow): number | string => {
    const { offer } = row;
    switch (sortKey) {
      case 'value':
        return row.value ?? -Infinity;
      case 'expense':
        return row.expense ?? -Infinity;
      case 'margin':
        return row.value === null || row.expense === null
          ? -Infinity
          : row.value - row.expense;
      case 'marginRatio':
        return row.expense === null
          ? -Infinity
          : getMarginRatio(row.value ?? 0, row.expense) ?? -Infinity;
      case 'reportedAt':
        return offer.reportedAt;
      case 'departmentName':
        return departmentOf(offer).toLowerCase();
      case 'workType':
        return workTypesLabel(row.workTypes).toLowerCase();
      case 'type':
        return typeLabel(offer.type).toLowerCase();
      case 'status':
        return PHASE_KEYS.indexOf(phaseOf(offer));
      default:
        return `${offer[sortKey] ?? ''}`.toLowerCase();
    }
  };
  const sorted = [...filtered].sort((a, b) => {
    const first = sortValue(a);
    const second = sortValue(b);
    let order = 0;
    if (first < second) {
      order = -1;
    } else if (first > second) {
      order = 1;
    }
    return sortAscending ? order : -order;
  });
  const shown = showAll ? sorted : sorted.slice(0, ROWS_SHOWN);

  const sortBy = (key: SortKey) => {
    if (key === sortKey) {
      setSortAscending(!sortAscending);
    } else {
      setSortKey(key);
      setSortAscending(!DESCENDING_FIRST.includes(key));
    }
  };

  const sortArrow = sortAscending ? ' ▲' : ' ▼';

  const header = (key: SortKey, label: string, numeric = false) => (
    <th
      className={`${numeric ? 'numeric ' : ''}${
        sortKey === key ? 'sorted' : ''
      }`}
      onClick={() => sortBy(key)}
    >
      {label}
      {sortKey === key ? sortArrow : ''}
    </th>
  );

  // An offer of many work types (of those searched) opens into a row for each
  const canOpen = (row: IRow) => row.workTypes.length > 1;
  const isOpen = (row: IRow) => canOpen(row) && opened.has(row.offer.id);
  const toggleOpen = (id: number) => {
    const next = new Set(opened);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setOpened(next);
  };

  const totals = sumOf(filtered);
  const totalRatio = ratioOf(totals);

  // The totals of each phase. A click chooses the phase alone (or all again), a click with
  // Ctrl (⌘ on a Mac) adds it to the chosen ones or takes it away.
  const phaseTiles = PHASE_KEYS.map((key) => {
    const sums = sumOf(
      withoutPhase.filter((row) => phaseOf(row.offer) === key)
    );
    return { key, ratio: ratioOf(sums), sums };
  });
  const choosePhase = (key: string, addToChosen: boolean) => {
    if (addToChosen) {
      setPhases(
        phases.includes(key)
          ? phases.filter((chosen) => chosen !== key)
          : [...phases, key]
      );
      return;
    }

    setPhases(phases.length === 1 && phases[0] === key ? [] : [key]);
  };

  const dayOf = (value: null | string) =>
    value ? new Date(value).toLocaleDateString('fi-FI') : '';

  const marginBadge = (ratio: null | number) =>
    ratio === null ? (
      '–'
    ) : (
      <span
        className="offer-dashboard-margin"
        style={{ borderColor: getMarginColor(ratio) }}
      >
        {`${formatGroupedNumber(ratio, 1, 1)} %`}
      </span>
    );

  // The view as shown: the rows in their order with the opened work types, sums as numbers
  const exportExcel = async () => {
    setExporting(true);
    try {
      const line = (
        offer: IOfferSalesOffer,
        groups: IOfferSalesWorkType[],
        value: null | number,
        expense: null | number
      ) => {
        const ratio =
          expense === null ? null : getMarginRatio(value ?? 0, expense);
        return [
          dayOf(offer.reportedAt),
          isSalesPro(offer)
            ? `${offer.targetName} (SalesPro ${offer.code ?? ''})`.trim()
            : offer.targetName,
          customerOf(offer),
          departmentOf(offer),
          offeredByOf(offer),
          typeLabel(offer.type),
          groups.map((group) => workTypeLabel(group.group)).join(', '),
          getOfferStatusLabel(t, offer.status, offer.type),
          value === null ? null : Math.round(value),
          expense === null ? null : Math.round(expense),
          value === null || expense === null
            ? null
            : Math.round(value - expense),
          ratio === null ? null : ratio / 100,
        ];
      };

      const response = await apiReports.exportReport({
        columns: [
          { title: tr('Reported at') },
          { title: tr('Target') },
          { title: tr('Customer') },
          { title: tr('Department') },
          { title: tr('Offered by') },
          { title: tr('Type') },
          { title: tr('Work type') },
          { title: tr('Status') },
          { numberFormat: '#,##0', title: `${tr('Sales')} ${report.symbol}` },
          {
            numberFormat: '#,##0',
            title: `${tr('Expense')} ${report.symbol}`,
          },
          {
            numberFormat: '#,##0',
            title: `${tr('Margin')} ${report.symbol}`,
          },
          { numberFormat: '0.0%', title: tr('Margin %') },
        ],
        fileName: tr('File name'),
        rows: sorted.flatMap((row) => [
          line(row.offer, row.workTypes, row.value, row.expense),
          ...(isOpen(row)
            ? row.workTypes.map((group) =>
                line(row.offer, [group], group.value, group.expense)
              )
            : []),
        ]),
        sheetName: tr('Title'),
      });

      // The name the server gave, e.g. Myyntiraportti_2026-10-21.xlsx
      const disposition = response.headers['content-disposition'] ?? '';
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = match ? decodeURIComponent(match[1]) : 'sales.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      dispatch(addMessage(tr('Export failed')));
    } finally {
      setExporting(false);
    }
  };

  const filter = (
    label: string,
    allLabel: string,
    value: string[],
    onChange: (value: string[]) => void,
    options: IFilterOption[]
  ) => (
    <MultiSelectFilter
      allLabel={allLabel}
      chosenLabel={(count) => tr('Chosen', { count })}
      label={label}
      onChange={onChange}
      options={options}
      value={value}
    />
  );

  const asOptions = (values: string[]): IFilterOption[] =>
    values.map((value) => ({ label: value, value }));

  // The sales of the period by day (a short period) or by month, in parts by phase
  const days = daysOf(period);
  const byDay = days.length <= MAX_DAY_COLUMNS;
  const chartColumns: IStackedColumn[] = (byDay ? days : monthsOf(period)).map(
    (start) => {
      const values: { [phase: string]: number } = {};
      filtered.forEach((row) => {
        const day = new Date(row.offer.reportedAt);
        const same =
          day.getFullYear() === start.getFullYear() &&
          day.getMonth() === start.getMonth() &&
          (!byDay || day.getDate() === start.getDate());
        if (same) {
          const phase = phaseOf(row.offer);
          values[phase] = (values[phase] ?? 0) + (row.value ?? 0);
        }
      });
      return {
        label: byDay
          ? `${start.getDate()}.${start.getMonth() + 1}.`
          : `${`${start.getMonth() + 1}`.padStart(
              2,
              '0'
            )}/${`${start.getFullYear()}`.slice(2)}`,
        values,
      };
    }
  );
  const chartSeries: ISeries[] = PHASE_KEYS.map((key) => ({
    color: PHASE_COLORS[key],
    key,
    label: tr(`phases.${key}`),
  }));

  // The signers with the largest sales, their margin % as a badge
  const signerBars = Array.from(
    filtered
      .reduce((bySigner, row) => {
        const key = offeredByOf(row.offer) || tr('No signer');
        bySigner.set(key, [...(bySigner.get(key) ?? []), row]);
        return bySigner;
      }, new Map<string, IRow[]>())
      .entries()
  )
    .map(([key, rows]) => {
      const sums = sumOf(rows);
      const ratio = ratioOf(sums);
      return {
        badge:
          ratio === null
            ? undefined
            : {
                color: getMarginColor(ratio),
                text: `${formatGroupedNumber(ratio, 1, 1)} %`,
              },
        color: PHASE_COLORS['offer-sent'],
        key,
        label: key,
        parts: PHASE_KEYS.map((phase) => ({
          color: PHASE_COLORS[phase],
          label: `${tr(`phases.${phase}`)}: ${money(
            sumOf(rows.filter((row) => phaseOf(row.offer) === phase)).value
          )}`,
          value: sumOf(rows.filter((row) => phaseOf(row.offer) === phase))
            .value,
        })),
        text: `${money(sums.value)} · ${sums.count}`,
        value: sums.value,
      };
    })
    .sort((a, b) => b.value - a.value)
    .slice(0, SIGNERS_CHARTED);

  // An offer of SalesPro is shown on its own tab
  const openOffer = (offer: IOfferSalesOffer) =>
    history.push(
      isSalesPro(offer) ? '?view=salespro' : `/offers/${offer.id}/${offer.type}`
    );

  // The sums of a row; an unknown expense of an offer of SalesPro is shown as unknown
  const sumCells = (
    restricted: boolean,
    value: null | number,
    expense: null | number
  ) => {
    if (restricted) {
      return (
        <>
          <td className="numeric">NDA</td>
          <td className="numeric">NDA</td>
          <td className="numeric">NDA</td>
          <td className="numeric">–</td>
        </>
      );
    }

    return (
      <>
        <td className="numeric">{money(value ?? 0)}</td>
        <td className="numeric">{expense === null ? '–' : money(expense)}</td>
        <td className="numeric">
          {expense === null ? '–' : money((value ?? 0) - expense)}
        </td>
        <td className="numeric">
          {marginBadge(
            expense === null ? null : getMarginRatio(value ?? 0, expense)
          )}
        </td>
      </>
    );
  };

  return (
    <PageContainer fullWidth>
      <div className="offer-dashboard">
        {tabs}
        <div className="offer-dashboard-header">
          <div>
            <h1>{tr('Title')}</h1>
            <p className="offer-dashboard-subtitle">{tr('Subtitle')}</p>
          </div>
          <div className="offer-dashboard-filters offer-sales-filters">
            {picker}
            <div className="offer-sales-filter-row">
              {filter(
                tr('Department'),
                tr('All departments'),
                departments,
                setDepartments,
                asOptions(departmentOptions)
              )}
              {filter(
                tr('Customer'),
                tr('All customers'),
                customers,
                setCustomers,
                asOptions(customerOptions)
              )}
              {filter(
                tr('Offered by'),
                tr('All signers'),
                signers,
                setSigners,
                asOptions(signerOptions)
              )}
              {filter(
                tr('Work type'),
                tr('All work types'),
                workTypes,
                setWorkTypes,
                workTypeOptions.map((group) => ({
                  label: workTypeLabel(group),
                  value: workTypeValue(group),
                }))
              )}
              {/* The phases, the same choice as the boxes below */}
              {filter(
                tr('Status'),
                tr('All statuses'),
                phases,
                setPhases,
                PHASE_KEYS.map((key) => ({
                  label: tr(`phases.${key}`),
                  value: key,
                }))
              )}
              {/* The open offers of SalesPro with those of ProBid */}
              <button
                aria-pressed={includeSalesPro}
                className={`offer-sales-salespro${
                  includeSalesPro ? ' active' : ''
                }`}
                onClick={toggleSalesPro}
                title={tr('Include SalesPro info')}
                type="button"
              >
                {includeSalesPro ? '✓ ' : ''}
                {tr('Include SalesPro')}
              </button>
              {/* The currency of the sums, with the exchange rate below it */}
              <CurrencyToggle
                base={report.currency}
                onChange={report.setCurrency}
                rate={report.rate}
                value={report.currency}
              />
              <button
                className="offer-dashboard-export"
                disabled={exporting || filtered.length === 0}
                onClick={exportExcel}
                type="button"
              >
                {exporting ? tr('Exporting') : tr('Export')}
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <p className="offer-dashboard-info">{tr('Loading')}</p>
        ) : null}
        {failed ? (
          <p className="offer-dashboard-info">{tr('Load failed')}</p>
        ) : null}

        {data !== null && !loading ? (
          <>
            {/* The totals of each phase; a click shows only its offers, Ctrl adds more */}
            <div className="offer-dashboard-kpis">
              {phaseTiles.map((tile) => (
                <button
                  aria-pressed={phases.includes(tile.key)}
                  className={`offer-dashboard-kpi offer-sales-phase${
                    phases.includes(tile.key) ? ' active' : ''
                  }`}
                  key={tile.key}
                  onClick={(event) =>
                    choosePhase(tile.key, event.ctrlKey || event.metaKey)
                  }
                  title={tr('Phase help')}
                  style={{ borderTopColor: PHASE_COLORS[tile.key] }}
                  type="button"
                >
                  <div className="offer-dashboard-kpi-label">
                    {tr(`phases.${tile.key}`)}
                  </div>
                  <div
                    className="offer-dashboard-kpi-value"
                    style={{ color: PHASE_COLORS[tile.key] }}
                  >
                    {money(tile.sums.value)}
                  </div>
                  <div className="offer-dashboard-kpi-note">
                    {tr('Phase note', {
                      count: tile.sums.count,
                      margin: money(tile.sums.margin),
                      ratio:
                        tile.ratio === null
                          ? '–'
                          : `${formatGroupedNumber(tile.ratio, 1, 1)} %`,
                    })}
                  </div>
                  <div className="offer-dashboard-kpi-note">
                    {tr(`phases.${tile.key} date`)}
                  </div>
                </button>
              ))}
            </div>

            <section className="offer-dashboard-card offer-dashboard-table-card">
              <h2>{tr('Offers', { count: filtered.length })}</h2>
              {workTypes.length > 0 ? (
                <p className="offer-dashboard-card-info">
                  {tr('Work type info', {
                    workType: workTypeOptions
                      .filter((group) =>
                        workTypes.includes(workTypeValue(group))
                      )
                      .map(workTypeLabel)
                      .join(', '),
                  })}
                </p>
              ) : null}
              <div className="offer-dashboard-table-wrapper">
                <table className="offer-dashboard-table">
                  <thead>
                    <tr>
                      {header('reportedAt', tr('Reported at'), true)}
                      {header('targetName', tr('Target'))}
                      {header('customerName', tr('Customer'))}
                      {header('departmentName', tr('Department'))}
                      {header('offeredBy', tr('Offered by'))}
                      {header('type', tr('Type'))}
                      {header('workType', tr('Work type'))}
                      {header('status', tr('Status'))}
                      {header('value', tr('Sales'), true)}
                      {header('expense', tr('Expense'), true)}
                      {header('margin', tr('Margin'), true)}
                      {header('marginRatio', tr('Margin %'), true)}
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((row) => {
                      const { offer } = row;
                      const restricted = offer.ndaRestricted;

                      return (
                        <React.Fragment key={rowKey(offer)}>
                          <tr onClick={() => openOffer(offer)}>
                            <td className="numeric">
                              {dayOf(offer.reportedAt)}
                            </td>
                            <td className="target">
                              {offer.targetName}
                              {isSalesPro(offer) ? (
                                <span
                                  className="offer-sales-source"
                                  title={offer.code ?? ''}
                                >
                                  SalesPro
                                </span>
                              ) : null}
                              {canOpen(row) ? (
                                <button
                                  aria-expanded={isOpen(row)}
                                  className="offer-sales-open"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    toggleOpen(offer.id);
                                  }}
                                  title={tr(
                                    isOpen(row)
                                      ? 'Hide work types'
                                      : 'Show work types'
                                  )}
                                  type="button"
                                >
                                  {isOpen(row) ? '−' : '+'}
                                </button>
                              ) : null}
                            </td>
                            <td>{customerOf(offer)}</td>
                            <td>{departmentOf(offer)}</td>
                            <td>{offeredByOf(offer)}</td>
                            <td>{typeLabel(offer.type)}</td>
                            <td>
                              {row.workTypes.length > 0
                                ? workTypesLabel(row.workTypes)
                                : ''}
                            </td>
                            <td>
                              <span
                                className="offer-dashboard-status"
                                style={{
                                  background: PHASE_COLORS[phaseOf(offer)],
                                }}
                              >
                                {getOfferStatusLabel(
                                  t,
                                  offer.status,
                                  offer.type
                                )}
                              </span>
                            </td>
                            {sumCells(restricted, row.value, row.expense)}
                          </tr>
                          {/* The offer by its work types */}
                          {isOpen(row)
                            ? row.workTypes.map((group) => (
                                <tr
                                  className="offer-sales-work-type"
                                  key={`${offer.id}-${group.group}`}
                                  onClick={() => openOffer(offer)}
                                >
                                  <td />
                                  <td className="target">{offer.targetName}</td>
                                  <td colSpan={4} />
                                  <td>{workTypeLabel(group.group)}</td>
                                  <td />
                                  <td className="numeric">
                                    {money(group.value)}
                                  </td>
                                  <td className="numeric">
                                    {money(group.expense)}
                                  </td>
                                  <td className="numeric">
                                    {money(group.value - group.expense)}
                                  </td>
                                  <td className="numeric">
                                    {marginBadge(
                                      getMarginRatio(group.value, group.expense)
                                    )}
                                  </td>
                                </tr>
                              ))
                            : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={8}>{tr('Total')}</td>
                      <td className="numeric">{money(totals.value)}</td>
                      <td className="numeric">{money(totals.expense)}</td>
                      <td className="numeric">{money(totals.margin)}</td>
                      <td className="numeric">{marginBadge(totalRatio)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {sorted.length > ROWS_SHOWN ? (
                <button
                  className="offer-dashboard-show-all"
                  onClick={() => setShowAll(!showAll)}
                  type="button"
                >
                  {showAll
                    ? t(`${translations.offers}:dashboard.Show less`)
                    : t(`${translations.offers}:dashboard.Show all`, {
                        count: sorted.length,
                      })}
                </button>
              ) : null}
            </section>

            {/* The offers of the table as charts: the sales by time and by signer */}
            <div className="offer-dashboard-grid">
              <section className="offer-dashboard-card wide">
                <h2>{tr(byDay ? 'Sales by day' : 'Sales by month')}</h2>
                <StackedColumns
                  columns={chartColumns}
                  format={(value) => money(value)}
                  series={chartSeries}
                />
                <Legend series={chartSeries} />
              </section>
              <section className="offer-dashboard-card">
                <h2>{tr('By signer')}</h2>
                <p className="offer-dashboard-card-info">
                  {tr('By signer info')}
                </p>
                {signerBars.length === 0 ? (
                  <p className="offer-dashboard-info">{tr('No offers')}</p>
                ) : (
                  <BarList
                    bars={signerBars}
                    onSelect={(key) =>
                      setSigners(
                        signers.length === 1 && signers[0] === key ? [] : [key]
                      )
                    }
                    selected={signers.length === 1 ? signers[0] : null}
                  />
                )}
              </section>
            </div>
          </>
        ) : null}
      </div>
    </PageContainer>
  );
};

export default OfferSales;
